import Header from '@/components/header/Header.vue';
import Footer from '@/components/footer/Footer.vue';
import axios from '@/config/axios';
import { useToast } from 'vue-toastification';
import { EMOJI_CATEGORIES, FUNNY_STICKERS, parseStickerContent, getStickerThemeClass, extractStickersAndText } from '@/constants/emojiStickers';
import { updateTrendingCacheIfBookExists } from '@/utils/trendingCache';
import * as bootstrap from 'bootstrap';
import { useHead } from '@vueuse/head'

export default {
  name: 'BookDetail',
  components: { Header, Footer },
  data() {
    return {
      book: null,
      isLoading: false,
      errorMessage: null,
      showFullContent: false,
      relatedBooks: [],
      isRelatedBooksLoading: false,
      reviews: [],
      reviewsPage: 0,
      reviewsTotalPages: 0,
      reviewsTotalElements: 0,
      isReviewsLoading: false,
      reviewsErrorMessage: null,
      myReviewData: null, // always pinned, fetched independently
      revealedSpoilers: [],
      showReviews: true,
      isLoggedIn: false,
      currentUserId: null,
      hasReviewedGlobal: false,
      isSubmitting: false,
      userReview: {
        content: '',
        isSpoil: false
      },
      isEditingMyReview: false,
      editingReviewContent: '',
      editingReviewIsSpoil: false,
      userRating: 0,
      hoverRating: 0,
      showWriteReviewForm: true,
      isSubmittingRating: false,
      commentsMap: {},
      isCommentsLoadingMap: {},
      commentInputsMap: {},
      isSubmittingCommentMap: {},
      showCommentsMap: {},
      isFullscreenReviewMap: {},
      focusedReview: null,
      reviewFocusModalInstance: null,
      commentsPageMap: {},
      commentsTotalPagesMap: {},
      replyInputsMap: {},
      showReplyFormsMap: {},
      submittingReplyMap: {},
      showRepliesMap: {},
      editingCommentMap: {},
      isEditingCommentMap: {},
      isSubmittingEditCommentMap: {},
      _scrollToCommentId: null,
      _scrollToRepliesId: null,
      selectedStickersMap: {}, // Maps input targets to selected sticker objects

      // Custom Delete Confirm Dialog state
      deleteConfirmModal: {
        show: false,
        type: null, // 'review' or 'comment'
        reviewId: null,
        commentId: null,
        title: '',
        message: '',
        isDeleting: false
      },

      // Custom Rating Confirm Dialog state
      ratingConfirmModal: {
        show: false,
        pendingRating: 0,
        oldRating: 0,
        isSubmitting: false
      },

      // After-action modals (review <-> rating cross-sell)
      afterRateReviewModal: { show: false }, // Mở sau khi rate xong, hỏi có muốn viết review
      afterReviewRateModal: { show: false, selectedRating: 0, hoverRating: 0 }, // Mở sau khi submit review, hỏi chấm mấy sao

      // Personal Book Note states
      showNoteModal: false,
      showNoteTextarea: false,
      collapseReadingStatus: false,
      collapseOwnership: false,
      isMobile: false,
      isLoadingNote: false,
      isSavingNote: false,
      originalBookNote: null,
      bookNote: {
        bookId: '',
        note: '',
        currentPage: 0,
        totalPage: 0,
        percent: 0.0,
        type: false, // false: page-based, true: percent-based
        progressStatus: 0, // 0: Not started, 1: Reading, 2: Read, 3: Paused
        ownStatus: 0 // 0: Not owned, 1: Paper book, 2: Ebook
      },
      progressStatuses: [
        { value: 0, label: 'Chưa đọc', icon: 'fa-solid fa-xmark' },
        { value: 1, label: 'Muốn đọc', icon: 'fa-solid fa-bookmark' },
        { value: 2, label: 'Đang đọc', icon: 'fa-solid fa-book-open' },
        { value: 3, label: 'Đã đọc xong', icon: 'fa-solid fa-circle-check' },
        { value: 4, label: 'Tạm ngưng', icon: 'fa-solid fa-circle-pause' }
      ],
      ownStatuses: [
        { value: 1, label: 'Sách giấy', icon: 'fa-solid fa-book' },
        { value: 2, label: 'Ebook', icon: 'fa-solid fa-tablet-screen-button' },
        { value: 0, label: 'Chưa sở hữu', icon: 'fa-solid fa-xmark' }
      ],

      // Format book detail modal states
      showFormatModal: false,
      formatModalTab: 'paper', // 'paper' | 'ebook'

      // Emoji & Funny Sticker Picker Popover state
      showEmojiPicker: false,
      activePickerTarget: null, // 'writeReview' | 'editReview' | 'newComment_ID' | 'editComment_ID' | 'replyComment_ID' | 'progressNote'
      emojiPickerTab: 'emojis', // 'emojis' | 'stickers'
      emojiPickerSearch: '',
      emojiPickerPos: { top: '0px', left: '0px' },

      emojiCategories: EMOJI_CATEGORIES,
      funnyStickers: FUNNY_STICKERS,

      // Old books
      hasOldBooks: false,
      oldBookCount: 0,
      head: null
    };
  },
  watch: {
    '$route.params.id': {
      handler(newId, oldId) {
        if (newId && newId !== oldId) {
          this.initPageData(newId);
        }
      }
    },
    '$route.query': {
      handler(newQuery) {
        if (newQuery && (newQuery.reviewId || newQuery.refId || newQuery.type)) {
          this.handleNotificationDeepLink();
        }
      },
      deep: true
    }
  },
  updated() {
    this.$nextTick(() => {
      document.querySelectorAll('.comment-input-field, .comment-reply-input').forEach(el => {
        if (!el.value) el.style.height = 'auto';
      });
    });
  },
  computed: {
    filteredStickers() {
      const q = (this.emojiPickerSearch || '').toLowerCase().trim();
      if (!q) return this.funnyStickers;
      return this.funnyStickers.filter(st =>
        (st.name && st.name.toLowerCase().includes(q)) ||
        (st.code && st.code.toLowerCase().includes(q))
      );
    },
    paginationRange() {
      const total = this.reviewsTotalPages;
      const current = this.reviewsPage;
      if (total <= 7) {
        return Array.from({ length: total }, (_, i) => i);
      }
      const pages = [];
      pages.push(0);
      if (current > 2) pages.push('...');
      const start = Math.max(1, current - 1);
      const end = Math.min(total - 2, current + 1);
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      if (current < total - 3) pages.push('...');
      pages.push(total - 1);
      return pages;
    },
    starBars() {
      if (!this.book) return [];
      return [
        { star: 5, count: this.book.star5 || 0 },
        { star: 4, count: this.book.star4 || 0 },
        { star: 3, count: this.book.star3 || 0 },
        { star: 2, count: this.book.star2 || 0 },
        { star: 1, count: this.book.star1 || 0 },
      ];
    },
    hadPreviousBookData() {
      if (!this.originalBookNote) return false;
      return Number(this.originalBookNote.progressStatus) !== 0 || Number(this.originalBookNote.ownStatus) !== 0;
    },
    isNoteModified() {
      if (!this.originalBookNote) return false;
      const m = this.bookNote;
      const o = this.originalBookNote;
      return (
        Number(m.progressStatus) !== Number(o.progressStatus) ||
        Number(m.ownStatus) !== Number(o.ownStatus) ||
        m.type !== o.type ||
        Number(m.currentPage) !== Number(o.currentPage) ||
        parseFloat(m.percent || 0) !== parseFloat(o.percent || 0) ||
        (m.note || '').trim() !== (o.note || '').trim()
      );
    },
    isDeleteAction() {
      return Number(this.bookNote.progressStatus) === 0 && Number(this.bookNote.ownStatus) === 0 && this.hadPreviousBookData;
    },
    hasExistingNote() {
      const note = (this.originalBookNote?.note || this.bookNote?.note || '').trim();
      return !!note && note !== '.' && note !== '-' && !note.startsWith('Cập nhật trạng thái: ');
    },
    hasReviewed() {
      return !!this.myReviewData || this.hasReviewedGlobal;
    },
    myReview() {
      return this.myReviewData;
    },
    otherReviews() {
      if (!this.myReviewData || !this.currentUserId) {
        return this.reviews;
      }
      return this.reviews.filter(r => String(r.reviewerId) !== String(this.currentUserId));
    },
    visualPercent() {
      const p = parseFloat(this.bookNote.percent);
      if (!isNaN(p)) {
        return Math.min(100, Math.max(0, Math.round(p * 10) / 10));
      }
      if (this.bookNote.totalPage > 0) {
        const pct = (this.bookNote.currentPage / this.bookNote.totalPage) * 100;
        return Math.min(100, Math.max(0, Math.round(pct * 10) / 10));
      }
      return 0;
    },
    noteValidationError() {
      if (this.bookNote.note && this.bookNote.note.length > 500) {
        return 'Ghi chú không được vượt quá 500 ký tự.';
      }
      // Only validate pages/percents if status is Reading (2) or Paused (4)
      if (Number(this.bookNote.progressStatus) === 2 || Number(this.bookNote.progressStatus) === 4) {
        if (!this.bookNote.type) {
          const curr = Number(this.bookNote.currentPage) || 0;
          const total = Number(this.bookNote.totalPage) || 0;
          if (curr < 0 || total < 0) {
            return 'Số trang không được âm.';
          }
          if (curr > total) {
            return 'Số trang hiện tại không thể lớn hơn tổng số trang.';
          }
        } else {
          const pct = parseFloat(this.bookNote.percent);
          if (isNaN(pct) || pct < 0 || pct > 100) {
            return 'Tỉ lệ % tiến độ phải từ 0 đến 100.';
          }
        }
      }
      return null;
    },
    savedVisualPercent() {
      const note = this.originalBookNote || this.bookNote;
      if (!note) return 0;
      const p = parseFloat(note.percent);
      if (!isNaN(p)) {
        return Math.min(100, Math.max(0, Math.round(p * 10) / 10));
      }
      if (note.totalPage > 0) {
        const pct = (note.currentPage / note.totalPage) * 100;
        return Math.min(100, Math.max(0, Math.round(pct * 10) / 10));
      }
      return 0;
    },
    progressButtonText() {
      if (!this.isLoggedIn) {
        return 'Đánh dấu sách';
      }
      const note = this.originalBookNote || this.bookNote;
      const status = note ? note.progressStatus : 0;
      switch (status) {
        case 1:
          return 'Muốn đọc';
        case 2:
          return `Đang đọc (${this.savedVisualPercent}%)`;
        case 3:
          return 'Đã đọc xong';
        case 4:
          return 'Tạm ngưng';
        default:
          return 'Ghi chú & Tiến độ';
      }
    },
    progressButtonIcon() {
      if (!this.isLoggedIn) {
        return 'fa-solid fa-book-bookmark';
      }
      const note = this.originalBookNote || this.bookNote;
      const status = note ? note.progressStatus : 0;
      switch (status) {
        case 1:
          return 'fa-solid fa-bookmark';
        case 2:
          return 'fa-solid fa-book-open';
        case 3:
          return 'fa-solid fa-circle-check';
        case 4:
          return 'fa-solid fa-circle-pause';
        default:
          return 'fa-solid fa-book-bookmark';
      }
    }
  },
  mounted() {
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
    this.isLoggedIn = localStorage.getItem('token') !== null;
    const currentAccountStr = localStorage.getItem('current-account');
    if (currentAccountStr) {
      try {
        const currentAccount = JSON.parse(currentAccountStr);
        this.currentUserId = currentAccount.id;
      } catch (e) {
        console.error('Error parsing current-account', e);
      }
    }
    const id = this.$route.params.id;
    this.initPageData(id);
    document.addEventListener('click', this.handleDocumentEmojiClick);
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile);
    document.removeEventListener('click', this.handleDocumentEmojiClick);
    if (this.head && typeof this.head.dispose === 'function') {
      this.head.dispose();
      this.head = null;
    }
  },
  methods: {
    initPageData(id) {
      if (!id) {
        this.errorMessage = 'Không tìm thấy ID sách.';
        return;
      }
      this.book = null;
      this.isLoading = true;
      this.errorMessage = null;
      this.reviews = [];
      this.reviewsPage = 0;
      this.reviewsTotalPages = 0;
      this.reviewsTotalElements = 0;
      this.myReviewData = null;
      this.hasReviewedGlobal = false;
      this.userRating = 0;
      this.hoverRating = 0;
      this.showWriteReviewForm = true;
      this.showFullContent = false;
      this.revealedSpoilers = [];
      this.showEmojiPicker = false;
      this.originalBookNote = null;
      this.afterRateReviewModal.show = false;
      this.afterReviewRateModal.show = false;
      this.afterReviewRateModal.selectedRating = 0;
      this.afterReviewRateModal.hoverRating = 0;

      window.scrollTo({ top: 0, behavior: 'smooth' });

      this.fetchBookDetail(id);
      this.fetchRelatedBooks(id);
      this.fetchBookReviews(id, 0).finally(() => {
        this.handleNotificationDeepLink();
      });
      this.fetchOldBookCount(id);
      if (this.isLoggedIn) {
        this.fetchBookNote(id);
        this.fetchUserRating(id);
        this.fetchMyReview(id);
      }
    },
    async handleNotificationDeepLink() {
      const query = this.$route ? this.$route.query : {};
      const reviewId = query.reviewId ? Number(query.reviewId) : null;
      const refId = query.refId ? Number(query.refId) : null;
      const type = query.type || '';
      const bookId = this.$route ? this.$route.params.id : null;

      if (!reviewId && !refId && !type) return;

      if (this.isReviewsLoading) {
        setTimeout(() => this.handleNotificationDeepLink(), 250);
        return;
      }

      // 1. Resolve review page if target review is not myReview
      let targetReviewPage = 0;
      const isMyReview = this.myReviewData && Number(this.myReviewData.id) === Number(reviewId);

      if (reviewId && !isMyReview && bookId) {
        try {
          const res = await axios.get('review/v1/get-page', {
            params: { bookId, reviewId }
          });
          if (res.data && res.data.status === '200' && res.data.result) {
            targetReviewPage = res.data.result.page || 0;
          }
        } catch (e) {
          console.warn('Could not fetch review page index', e);
        }
      }

      if (targetReviewPage !== this.reviewsPage) {
        await this.fetchBookReviews(bookId, targetReviewPage);
      }

      // 2. Resolve comment page & root comment if refId is specified
      const isCommentRelated = type.includes('COMMENT') || type.includes('REPLY');
      let targetCommentPage = 0;
      let targetRootId = null;

      if (reviewId && isCommentRelated) {
        this.showCommentsMap = Object.assign({}, this.showCommentsMap, { [reviewId]: true });

        if (refId) {
          try {
            const res = await axios.get('comment/v1/get-page', {
              params: { reviewId, commentId: refId }
            });
            if (res.data && res.data.status === '200' && res.data.result) {
              targetCommentPage = res.data.result.page || 0;
              targetRootId = res.data.result.rootId || null;
            }
          } catch (e) {
            console.warn('Could not fetch comment page index', e);
          }
        }

        // Fetch comments for the resolved comment page
        await this.fetchComments(reviewId, targetCommentPage);

        // Expand nested reply thread if target is a reply
        if (targetRootId && targetRootId !== refId) {
          this.showRepliesMap = Object.assign({}, this.showRepliesMap, { [targetRootId]: true });
        } else if (refId && this.commentsMap[reviewId]) {
          for (const comment of this.commentsMap[reviewId]) {
            const isReply = comment.replies && comment.replies.some(r => Number(r.id) === Number(refId));
            if (isReply) {
              this.showRepliesMap = Object.assign({}, this.showRepliesMap, { [comment.id]: true });
              break;
            }
          }
        }
      }

      // 3. Find target DOM element and smooth scroll + highlight
      this.$nextTick(() => {
        let attempts = 0;
        const findAndScroll = () => {
          attempts++;
          let targetEl = null;

          if (refId) {
            targetEl = document.getElementById(`comment-${refId}`) ||
                       document.querySelector(`[data-comment-id="${refId}"]`) ||
                       document.querySelector(`[data-reply-id="${refId}"]`);
          }

          if (!targetEl && reviewId) {
            targetEl = document.getElementById(`review-${reviewId}`) ||
                       document.querySelector(`[data-review-id="${reviewId}"]`);
          }

          if (!targetEl && attempts < 10) {
            setTimeout(findAndScroll, 150);
            return;
          }

          if (!targetEl) {
            targetEl = document.getElementById('reviews-section');
          }

          if (targetEl) {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });

            targetEl.classList.remove('noti-target-highlight');
            void targetEl.offsetWidth; // trigger reflow
            targetEl.classList.add('noti-target-highlight');

            setTimeout(() => {
              targetEl.classList.remove('noti-target-highlight');
            }, 3500);
          }
        };

        setTimeout(findAndScroll, 150);
      });
    },
    formatContentHtml(str) {
      return parseStickerContent(str, this.funnyStickers);
    },
    handleDocumentEmojiClick(e) {
      if (!this.showEmojiPicker) return;
      const popover = document.querySelector('.emoji-sticker-picker-popover');
      const triggers = document.querySelectorAll('.btn-emoji-trigger');
      let isTrigger = false;
      triggers.forEach(t => {
        if (t.contains(e.target)) isTrigger = true;
      });
      if (popover && !popover.contains(e.target) && !isTrigger) {
        this.closeEmojiPicker();
      }
    },
    toggleEmojiPicker(targetKey, event) {
      if (this.showEmojiPicker && this.activePickerTarget === targetKey) {
        this.closeEmojiPicker();
        return;
      }
      this.activePickerTarget = targetKey;
      this.showEmojiPicker = true;
      this.emojiPickerTab = 'emojis';
      this.emojiPickerSearch = '';

      if (event && event.currentTarget) {
        const rect = event.currentTarget.getBoundingClientRect();
        const popoverHeight = 365;

        // Position ABOVE the trigger button unless near the top edge of screen (< 120px)
        if (rect.top > 120) {
          const topPos = Math.max(10, rect.top + window.scrollY - popoverHeight - 6);
          const leftPos = Math.min(window.innerWidth - 345, Math.max(10, rect.left + window.scrollX - 60));
          this.emojiPickerPos = {
            top: `${topPos}px`,
            left: `${leftPos}px`
          };
        } else {
          const topPos = rect.bottom + window.scrollY + 6;
          const leftPos = Math.min(window.innerWidth - 345, Math.max(10, rect.left + window.scrollX - 60));
          this.emojiPickerPos = {
            top: `${topPos}px`,
            left: `${leftPos}px`
          };
        }
      }
    },
    closeEmojiPicker() {
      this.showEmojiPicker = false;
      this.activePickerTarget = null;
    },
    insertEmoji(char) {
      const target = this.activePickerTarget;
      if (!target) return;

      if (target === 'writeReview') {
        this.userReview.content = (this.userReview.content || '') + char;
      } else if (target === 'editReview') {
        this.editingReviewContent = (this.editingReviewContent || '') + char;
      } else if (target.startsWith('newComment_')) {
        const reviewId = target.replace('newComment_', '');
        this.commentInputsMap[reviewId] = (this.commentInputsMap[reviewId] || '') + char;
      } else if (target.startsWith('editComment_')) {
        const commentId = target.replace('editComment_', '');
        this.editingCommentMap[commentId] = (this.editingCommentMap[commentId] || '') + char;
      } else if (target.startsWith('replyComment_')) {
        const commentId = target.replace('replyComment_', '');
        this.replyInputsMap[commentId] = (this.replyInputsMap[commentId] || '') + char;
      } else if (target === 'progressNote') {
        this.bookNote.note = (this.bookNote.note || '') + char;
      }
    },
    insertSticker(sticker) {
      if (!this.activePickerTarget) return;
      const currentList = this.selectedStickersMap[this.activePickerTarget] || [];
      if (currentList.length >= 5) {
        const toast = useToast();
        toast.warning('Bạn chỉ được chọn tối đa 5 sticker cho mỗi bình luận!');
        return;
      }
      this.selectedStickersMap = {
        ...this.selectedStickersMap,
        [this.activePickerTarget]: [...currentList, sticker]
      };
    },
    getStickerThemeClass(sticker) {
      const text = typeof sticker === 'string' ? sticker : (sticker?.name || sticker?.code || '');
      return getStickerThemeClass(text);
    },
    areStickersEqual(s1, s2) {
      const list1 = s1 || [];
      const list2 = s2 || [];
      if (list1.length !== list2.length) return false;
      for (let i = 0; i < list1.length; i++) {
        const k1 = typeof list1[i] === 'string' ? list1[i] : (list1[i].name || list1[i].code || list1[i].url);
        const k2 = typeof list2[i] === 'string' ? list2[i] : (list2[i].name || list2[i].code || list2[i].url);
        if (k1 !== k2) return false;
      }
      return true;
    },
    isReviewChanged() {
      if (!this.myReview) return false;
      const { stickers: origStickers, cleanText: origText } = extractStickersAndText(this.myReview.content, this.funnyStickers);
      const currentText = (this.editingReviewContent || '').trim();
      const currentStickers = this.selectedStickersMap['editReview'] || [];

      const isTextOrStickersChanged = currentText !== origText || !this.areStickersEqual(origStickers, currentStickers);
      const isSpoilChanged = Boolean(this.editingReviewIsSpoil) !== Boolean(this.myReview.isSpoil);

      const hasContent = currentText.length > 0 || currentStickers.length > 0;
      return (isTextOrStickersChanged || isSpoilChanged) && hasContent;
    },
    isCommentChanged(comment) {
      if (!comment) return false;
      const { stickers: origStickers, cleanText: origText } = extractStickersAndText(comment.content, this.funnyStickers);
      const currentText = (this.editingCommentMap[comment.id] || '').trim();
      const currentStickers = this.selectedStickersMap['editComment_' + comment.id] || [];

      const isChanged = currentText !== origText || !this.areStickersEqual(origStickers, currentStickers);
      const hasContent = currentText.length > 0 || currentStickers.length > 0;

      return isChanged && hasContent;
    },
    removeSelectedSticker(target, index) {
      if (index === undefined) {
        const newMap = { ...this.selectedStickersMap };
        delete newMap[target];
        this.selectedStickersMap = newMap;
      } else {
        const currentList = this.selectedStickersMap[target] || [];
        const newList = currentList.filter((_, i) => i !== index);
        if (newList.length === 0) {
          const newMap = { ...this.selectedStickersMap };
          delete newMap[target];
          this.selectedStickersMap = newMap;
        } else {
          this.selectedStickersMap = {
            ...this.selectedStickersMap,
            [target]: newList
          };
        }
      }
    },
    getStickerString(target) {
      const stickers = this.selectedStickersMap[target] || [];
      return stickers.map(sticker => {
        if (sticker.name || sticker.code) return ` [sticker: ${sticker.name || sticker.code}] `;
        if (sticker.url) return ` [sticker: ${sticker.url}] `;
        return '';
      }).join('');
    },
    clearSelectedSticker(target) {
      this.removeSelectedSticker(target);
    },
    fetchOldBookCount(id) {
      if (!id) return;
      axios.get(`old-book/v1/count-by-book/${id}`)
        .then(res => {
          if (res.data.status === '200' && res.data.result !== undefined) {
            const count = Number(res.data.result) || 0;
            this.oldBookCount = count;
            this.hasOldBooks = count > 0;
          }
        })
        .catch(() => {
          axios.get('old-book/v1/search', { params: { page: 0, idBook: id } })
            .then(res => {
              if (res.data.status === '200' && res.data.result) {
                const count = res.data.result.totalElements || 0;
                this.oldBookCount = count;
                this.hasOldBooks = count > 0;
              }
            });
        });
    },
    fetchRelatedBooks(id) {
      if (!id) return;
      this.isRelatedBooksLoading = true;
      this.relatedBooks = [];
      axios.get(`book/v1/related-books/${id}`)
        .then(res => {
          if (res.data?.status === '200' && Array.isArray(res.data.result)) {
            this.relatedBooks = res.data.result;
          }
        })
        .catch(err => {
          console.error('Error fetching related books', err);
        })
        .finally(() => {
          this.isRelatedBooksLoading = false;
        });
    },
    formatRelatedAuthors(authorsStr) {
      if (!authorsStr) return '';
      if (authorsStr.includes(':')) {
        const names = [];
        authorsStr.split(';').forEach(part => {
          const tokens = part.split(':');
          if (tokens.length >= 2 && tokens[1]) {
            names.push(tokens[1]);
          }
        });
        return names.join(', ');
      }
      return authorsStr;
    },
    formatRelatedCategory(categoriesStr) {
      if (!categoriesStr) return '';
      if (categoriesStr.includes(':')) {
        const first = categoriesStr.split(';')[0];
        const tokens = first.split(':');
        if (tokens.length >= 2 && tokens[1]) {
          return tokens[1];
        }
      }
      return categoriesStr.split(',')[0];
    },
    formatCoverUrl(cover) {
      if (!cover) return '/img/default-book.png';
      if (cover.startsWith('data:') || cover.startsWith('http')) return cover;
      return 'data:image/png;base64,' + cover;
    },
    fetchBookDetail(id, silent = false) {
      if (!silent) {
        this.isLoading = true;
        this.errorMessage = null;
      }

      axios.get(`book/v1/detail/${id}`)
        .then(response => {
          const data = response.data;
          if (data.status === '200' && data.result) {
            const bookData = data.result;
            if (bookData.cover && !(bookData.cover.startsWith('data:') || bookData.cover.startsWith('http'))) {
              bookData.cover = 'data:image/png;base64,' + bookData.cover;
            }
            const state = this.$route.state || {};
            this.book = { ...state, ...bookData };
            if (this.head && typeof this.head.dispose === 'function') {
              this.head.dispose();
            }
            if (this.book && this.book.name) {
              document.title = `${this.book.name} - Lối Đọc`;
            }
            this.head = useHead({
              title: this.book.name ? `${this.book.name} - Lối Đọc` : 'Lối Đọc',
              meta: [
                {
                  name: 'description',
                  content: this.truncate(this.book.content)
                }
              ],
              link: [
                {
                  rel: 'canonical',
                  href: `${window.location.origin}${this.$route.path}`
                }
              ]
            })
            // userRating is managed by fetchUserRating (called on mount & after submit)
            // Only set from bookDetail if fetchUserRating hasn't set it yet
            if (!this.userRating) {
              this.userRating = bookData.userRating || 0;
            }
          } else if (data.status === '404') {
            if (!silent) {
              this.errorMessage = 'Không tìm thấy cuốn sách này.';
            }
          } else {
            if (!silent) {
              this.errorMessage = data.message || 'Đã xảy ra lỗi khi tải thông tin sách.';
            }
          }
        })
        .catch(error => {
          if (!silent) {
            this.errorMessage = error.response?.data?.message || 'Không thể kết nối đến máy chủ.';
          }
        })
        .finally(() => {
          if (!silent) {
            this.isLoading = false;
          }
        });
    },
    truncate(text, maxLength = 180) {
      text = text.trim().replace(/\s+/g, ' ')
      if (text.length <= maxLength) return text
      const truncated = text.slice(0, maxLength)
      const lastSpace = truncated.lastIndexOf(' ')
      return (lastSpace > 0 ? truncated.slice(0, lastSpace) : truncated) + '...'
},
    getAuthorsString() {
      if (!this.book?.authors || this.book.authors.length === 0) return 'Khuyết danh';
      return this.book.authors.map(a => a.name).join(', ');
    },
    getTranslatorsString() {
      if (!this.book?.translators || this.book.translators.length === 0) return '';
      return this.book.translators.map(t => t.name).join(', ');
    },
    getCategoriesList() {
      if (!this.book) return [];
      if (Array.isArray(this.book.categories) && this.book.categories.length > 0) {
        return this.book.categories.map(c => typeof c === 'object' ? (c.name || c.categoryName || c.title) : c).filter(Boolean);
      }
      if (Array.isArray(this.book.genres) && this.book.genres.length > 0) {
        return this.book.genres.map(g => typeof g === 'object' ? (g.name || g.genreName || g.title) : g).filter(Boolean);
      }
      if (this.book.category) {
        const catName = typeof this.book.category === 'string' ? this.book.category : (this.book.category.name || this.book.category.categoryName);
        if (catName) return [catName];
      }
      if (this.book.categoryName) return [this.book.categoryName];
      if (this.book.genre) return [this.book.genre];
      return [];
    },
    getCategoryObjects() {
      if (!this.book) return [];
      const list = [];
      if (Array.isArray(this.book.categories) && this.book.categories.length > 0) {
        this.book.categories.forEach(c => {
          if (typeof c === 'object' && c !== null) {
            const name = c.name || c.categoryName || c.title || '';
            if (name) list.push({ id: c.id || null, name });
          } else if (typeof c === 'string' && c.trim()) {
            list.push({ id: null, name: c.trim() });
          }
        });
      } else if (Array.isArray(this.book.genres) && this.book.genres.length > 0) {
        this.book.genres.forEach(g => {
          if (typeof g === 'object' && g !== null) {
            const name = g.name || g.genreName || g.title || '';
            if (name) list.push({ id: g.id || null, name });
          } else if (typeof g === 'string' && g.trim()) {
            list.push({ id: null, name: g.trim() });
          }
        });
      } else if (this.book.category) {
        if (typeof this.book.category === 'object') {
          list.push({ id: this.book.category.id || null, name: this.book.category.name || this.book.category.categoryName || '' });
        } else if (typeof this.book.category === 'string' && this.book.category.trim()) {
          list.push({ id: null, name: this.book.category.trim() });
        }
      } else if (this.book.categoryName) {
        list.push({ id: this.book.categoryId || null, name: String(this.book.categoryName).trim() });
      }
      return list.filter(item => item.name);
    },
    async goSearchAuthor(author) {
      if (!author) return;
      const authorId = typeof author === 'object' ? (author.id || author.authorId) : null;
      const authorName = typeof author === 'object' ? (author.name || author.authorName || '') : String(author);
      const cleanName = authorName ? authorName.trim() : '';

      if (authorId) {
        this.$router.push({
          path: `/author/${authorId}`,
          state: { authorName: cleanName }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
        return;
      }

      if (!cleanName) return;

      try {
        const res = await axios.get('author/v1/search', {
          params: { key: cleanName, page: 0 },
          _skip403Redirect: true
        });
        let list = [];
        if (res.data && res.data.status === '200' && res.data.result) {
          if (Array.isArray(res.data.result.content)) {
            list = res.data.result.content;
          } else if (Array.isArray(res.data.result)) {
            list = res.data.result;
          }
        }
        if (list.length > 0) {
          const match = list.find(a => a.name && a.name.trim().toLowerCase() === cleanName.toLowerCase()) || list[0];
          if (match && match.id) {
            this.$router.push({
              path: `/author/${match.id}`,
              state: { authorName: match.name || cleanName }
            }).catch(err => {
              if (err.name !== 'NavigationDuplicated') console.error(err);
            });
            return;
          }
        }
      } catch (e) {
        console.error('Error finding author by name:', e);
      }

      this.$router.push({ path: '/search', state: { key: cleanName, page: 0 } });
    },
    async goSearchCategory(cat) {
      if (!cat) return;
      const catId = typeof cat === 'object' ? (cat.id || cat.categoryId) : null;
      const catName = typeof cat === 'object' ? (cat.name || cat.categoryName || '') : String(cat);
      const cleanName = catName ? catName.trim() : '';

      if (catId) {
        this.$router.push({
          path: `/category/${catId}`,
          state: { categoryName: cleanName }
        }).catch(err => {
          if (err.name !== 'NavigationDuplicated') console.error(err);
        });
        return;
      }

      if (!cleanName) return;

      try {
        const res = await axios.get('category/v1/get-all', { _skip403Redirect: true });
        if (res.data && res.data.status === '200' && Array.isArray(res.data.result)) {
          const categories = res.data.result;
          const match = categories.find(c => c.name && c.name.trim().toLowerCase() === cleanName.toLowerCase()) ||
                        categories.find(c => c.name && c.name.trim().toLowerCase().includes(cleanName.toLowerCase()));
          if (match && match.id) {
            this.$router.push({
              path: `/category/${match.id}`,
              state: { categoryName: match.name || cleanName }
            }).catch(err => {
              if (err.name !== 'NavigationDuplicated') console.error(err);
            });
            return;
          }
        }
      } catch (e) {
        console.error('Error finding category by name:', e);
      }

      this.$router.push({ path: '/search', state: { key: cleanName, page: 0 } });
    },
    goBack() {
      if (window.history.state && window.history.state.back) {
        this.$router.back();
      } else {
        const lastSearchKey = sessionStorage.getItem('lastSearchKey');
        const lastSearchPage = sessionStorage.getItem('lastSearchPage') || 0;
        if (lastSearchKey) {
          this.$router.push({
            path: '/search',
            state: { key: lastSearchKey, page: parseInt(lastSearchPage, 10) || 0 }
          });
        } else {
          this.$router.push('/');
        }
      }
    },
    getStarPercent(val) {
      const total = this.book?.nov || 1;
      return Math.round((val / total) * 100);
    },
    getFallbackClass() {
      const idStr = String(this.book?.id || this.book?.name || '1');
      let hash = 0;
      for (let i = 0; i < idStr.length; i++) {
        hash = idStr.charCodeAt(i) + ((hash << 5) - hash);
      }
      return `fallback-cover-variant-${(Math.abs(hash) % 4) + 1}`;
    },
    fetchBookReviews(id, page = 0) {
      this.isReviewsLoading = true;
      this.reviewsErrorMessage = null;

      return axios.get(`review/v1/get-by-book/${id}`, {
        params: { page }
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200' && data.result) {
            const reviews = data.result.content || [];
            reviews.forEach(review => {
              // Normalize spoil field
              review.isSpoil = review.spoil ?? review.isSpoil ?? false;
              // Normalize user interaction status
              review.userInteract = review.userInteract ?? review.interact ?? null;
              // Normalize isEdited field (BE Jackson serializes getIsEdited() or isEdited to `edited`)
              review.isEdited = review.isEdited || review.edited || review.is_edited ? true : false;
              // Normalize reviewer rating (BE LEFT JOIN evaluate, may be absent -> 0)
              review.reviewerRating = Number(review.reviewerRating) || 0;
              if (review.commentCount === undefined || review.commentCount === null) {
                review.commentCount = 0;
              }
              if (this.showCommentsMap[review.id] === undefined) {
                this.showCommentsMap[review.id] = false;
                this.commentsMap[review.id] = [];
                this.isCommentsLoadingMap[review.id] = false;
                this.commentInputsMap[review.id] = '';
                this.isSubmittingCommentMap[review.id] = false;
                this.commentsPageMap[review.id] = 0;
                this.commentsTotalPagesMap[review.id] = 0;
              }
            });

            this.reviews = reviews;
            this.reviewsPage = data.result.pageable?.pageNumber || 0;
            const pageSize = data.result.size || 10;
            const totalElements = Math.max(0, data.result.totalElements || 0);
            this.reviewsTotalPages = pageSize > 0 ? Math.ceil(totalElements / pageSize) : 0;
            this.reviewsTotalElements = totalElements;

            // Fetch comment counts for all community reviews in parallel so counts show immediately
          } else if (data.status === '404' || data.status === '500') {
            this.reviews = [];
            this.reviewsTotalPages = 0;
            this.reviewsTotalElements = 0;
            if (data.status === '500') {
              this.reviewsErrorMessage = data.message || 'Không thể tải bình luận.';
            }
          } else {
            this.reviewsErrorMessage = data.message || 'Đã xảy ra lỗi khi tải đánh giá.';
          }
        })
        .catch(error => {
          console.error('Fetch reviews error:', error);
          this.reviewsErrorMessage = error.response?.data?.message || 'Không thể kết nối để lấy danh sách đánh giá.';
        })
        .finally(() => {
          this.isReviewsLoading = false;
        });
    },
    // Fetch comment counts for all reviews in parallel so the badge shows without user clicking
    changeReviewsPage(page) {
      const id = this.$route.params.id;
      if (id && page >= 0 && page < this.reviewsTotalPages) {
        this.fetchBookReviews(id, page).then(() => {
          const el = document.getElementById('reviews-section');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    toggleSpoiler(reviewId) {
      if (this.revealedSpoilers.includes(reviewId)) {
        this.revealedSpoilers = this.revealedSpoilers.filter(id => id !== reviewId);
      } else {
        this.revealedSpoilers.push(reviewId);
      }
    },
    isSpoilerRevealed(reviewId) {
      return this.revealedSpoilers.includes(reviewId);
    },
    toggleReviews() {
      this.showReviews = !this.showReviews;
      if (this.showReviews) {
        const id = this.$route.params.id;
        if (id) {
          this.fetchBookReviews(id, 0);
          if (this.isLoggedIn) {
            this.fetchMyReview(id);
          }
        }
        this.$nextTick(() => {
          const el = document.getElementById('reviews-section');
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      }
    },
    openReviewsAndScroll() {
      if (!this.showReviews) {
        this.showReviews = true;
        const id = this.$route.params.id;
        if (id) {
          this.fetchBookReviews(id, 0);
        }
      }
      this.$nextTick(() => {
        const el = document.getElementById('reviews-section');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    },
    submitReview() {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập để viết nhận xét!');
        this.$router.push('/login');
        return;
      }

      const stickerString = this.getStickerString('writeReview');
      const finalContent = ((this.userReview.content || '') + stickerString).trim();

      if (!finalContent) {
        const toast = useToast();
        toast.error('Nội dung nhận xét không được để trống.');
        return;
      }

      this.isSubmitting = true;
      const toast = useToast();

      axios.post('review/create-or-update', {
        bookId: this.book.id,
        content: finalContent,
        spoil: this.userReview.isSpoil
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            toast.success(data.message || 'Viết nhận xét thành công.');
            this.userReview.content = '';
            this.userReview.isSpoil = false;
            this.clearSelectedSticker('writeReview');
            this.showWriteReviewForm = false;
            this.hasReviewedGlobal = true;
            this.fetchBookDetail(this.book.id, true);
            this.fetchBookReviews(this.book.id, 0);
            this.fetchMyReview(this.book.id);
            this.autoUpdateReadingStatusToRead();
            updateTrendingCacheIfBookExists(this.book.id);
            // Chỉ hỏi chấm sao nếu user chưa từng đánh giá
            if (!this.userRating) {
              this.afterReviewRateModal.selectedRating = 0;
              this.afterReviewRateModal.show = true;
            }
          } else {
            toast.error(data.message || 'Đã xảy ra lỗi khi lưu nhận xét.');
          }
        })
        .catch(error => {
          console.error('Submit review error:', error);
          toast.error(error.response?.data?.message || 'Không thể kết nối đến máy chủ.');
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    submitRating(ratingValue) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập để đánh giá sách!');
        this.$router.push('/login');
        return;
      }

      if (this.isSubmittingRating || this.ratingConfirmModal.isSubmitting) return;

      // Check if user has already rated this book (2nd time onwards)
      if (this.userRating > 0) {
        // If clicking the same rating star, do not show update modal or re-submit
        if (Number(this.userRating) === Number(ratingValue)) {
          return;
        }
        this.ratingConfirmModal = {
          show: true,
          pendingRating: ratingValue,
          oldRating: this.userRating,
          isSubmitting: false
        };
        return;
      }

      // First time rating -> proceed directly
      this.executeRatingSubmit(ratingValue);
    },
    closeRatingConfirmModal() {
      if (this.ratingConfirmModal.isSubmitting) return;
      this.ratingConfirmModal.show = false;
    },
    confirmRatingChange() {
      if (this.ratingConfirmModal.isSubmitting) return;
      this.executeRatingSubmit(this.ratingConfirmModal.pendingRating);
    },
    closeAfterRateModal() {
      this.afterRateReviewModal.show = false;
    },
    goToWriteReviewFromModal() {
      this.afterRateReviewModal.show = false;
      this.showWriteReviewForm = true;
      this.$nextTick(() => {
        const target = document.querySelector('.review-compose-area') || document.getElementById('reviews-section');
        if (!target) return;
        const stickyHeader = document.querySelector('.sticky-header-container');
        const offset = stickyHeader ? stickyHeader.offsetHeight : 0;
        const top = target.getBoundingClientRect().top + window.pageYOffset - offset - 16;
        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      });
    },
    closeAfterReviewModal() {
      if (this.isSubmittingRating) return;
      this.afterReviewRateModal.show = false;
      this.afterReviewRateModal.selectedRating = 0;
      this.afterReviewRateModal.hoverRating = 0;
    },
    submitRatingFromReviewModal() {
      if (this.isSubmittingRating || !this.afterReviewRateModal.selectedRating) return;
      this.executeRatingSubmit(this.afterReviewRateModal.selectedRating);
    },
    executeRatingSubmit(ratingValue) {
      this.isSubmittingRating = true;
      if (this.ratingConfirmModal.show) {
        this.ratingConfirmModal.isSubmitting = true;
      }
      const toast = useToast();

      axios.post('evaluate/rate', {
        bookId: this.book.id,
        evaluateNumber: ratingValue
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            this.userRating = ratingValue;
            // Cập nhật ngay số sao hiển thị trên review của mình (không cần load lại trang)
            if (this.myReviewData) {
              this.myReviewData.reviewerRating = ratingValue;
            }
            const ownCommunityReview = this.reviews.find(r => r.reviewerId === this.currentUserId);
            if (ownCommunityReview) {
              ownCommunityReview.reviewerRating = ratingValue;
            }
            if (this.focusedReview && (this.focusedReview.reviewerId === this.currentUserId)) {
              this.focusedReview.reviewerRating = ratingValue;
            }
            this.fetchBookDetail(this.book.id, true);
            this.autoUpdateReadingStatusToRead();
            updateTrendingCacheIfBookExists(this.book.id);
            this.ratingConfirmModal.show = false;
            this.afterReviewRateModal.show = false;
            // Chỉ hỏi viết review nếu user chưa từng viết
            if (!this.hasReviewed) {
              this.afterRateReviewModal.show = true;
            }
          } else {
            toast.error(data.message || 'Đã xảy ra lỗi khi lưu đánh giá.');
          }
        })
        .catch(error => {
          console.error('Submit rating error:', error);
          toast.error(error.response?.data?.message || 'Không thể kết nối để gửi đánh giá.');
        })
        .finally(() => {
          this.isSubmittingRating = false;
          this.ratingConfirmModal.isSubmitting = false;
        });
    },
    // Fetch user's own rating for this book (only called when logged in)
    fetchUserRating(bookId) {
      axios.get(`evaluate/get-by-book/${bookId}`)
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            this.userRating = data.result || 0;
          } else if (data.status === '404') {
            this.userRating = 0;
          }
        })
        .catch(() => {
          this.userRating = 0;
        });
    },
    // Fetch user's own review for this book via the dedicated personal endpoint
    fetchMyReview(bookId) {
      if (!this.isLoggedIn || !this.currentUserId) return;

      axios.get(`review/get-my-reviews`, { params: { bookId } })
        .then(response => {
          const data = response.data;
          if (data.status === '200' && Array.isArray(data.result) && data.result.length > 0) {
            const found = data.result[0];
            // Normalize spoil field (BE returns `spoil`, template uses `isSpoil`)
            found.isSpoil = found.spoil ?? found.isSpoil ?? false;
            // Normalize user interaction status
            found.userInteract = found.userInteract ?? found.interact ?? null;
            // Normalize isEdited field (BE returns `edited` or `isEdited`)
            found.isEdited = found.isEdited || found.edited || found.is_edited ? true : false;
            // Normalize reviewer rating (BE may return `reviewerRating` or nothing -> 0)
            found.reviewerRating = Number(found.reviewerRating) || 0;
            this.myReviewData = found;
            if (this.showCommentsMap[found.id] === undefined) {
              this.showCommentsMap[found.id] = false;
              this.commentsMap[found.id] = [];
              this.isCommentsLoadingMap[found.id] = false;
              this.commentInputsMap[found.id] = '';
              this.isSubmittingCommentMap[found.id] = false;
              this.commentsPageMap[found.id] = 0;
              this.commentsTotalPagesMap[found.id] = 0;
            }
            this.hasReviewedGlobal = true;
            this.showWriteReviewForm = false;
          } else {
            this.myReviewData = null;
            this.hasReviewedGlobal = false;
            this.showWriteReviewForm = true;
          }
        })
        .catch(() => {
          this.myReviewData = null;
          this.showWriteReviewForm = true;
        });
    },
    openReviewFocusModal(review) {
      this.focusedReview = review;
      // Ensure we fetch comments if not loaded yet
      if (!this.showCommentsMap[review.id]) {
        this.toggleComments(review.id);
      }
      this.$nextTick(() => {
        if (!this.reviewFocusModalInstance) {
          this.reviewFocusModalInstance = new bootstrap.Modal(this.$refs.reviewFocusModal, {
            backdrop: 'static',
            keyboard: false
          });
          if (this.$refs.reviewFocusModal) {
            this.$refs.reviewFocusModal.addEventListener('hide.bs.modal', () => {
              if (document.activeElement && typeof document.activeElement.blur === 'function') {
                document.activeElement.blur();
              }
            });
          }
        }
        this.reviewFocusModalInstance.show();
      });
    },
    toggleFullscreenReview(reviewId) {
      const review = (this.reviews && this.reviews.find(r => r.id === reviewId)) ||
        (this.myReviewData && this.myReviewData.id === reviewId ? this.myReviewData : null) ||
        (this.focusedReview && this.focusedReview.id === reviewId ? this.focusedReview : null);
      if (review) {
        this.openReviewFocusModal(review);
      }
    },
    toggleComments(reviewId) {
      const nextState = !this.showCommentsMap[reviewId];
      this.showCommentsMap = Object.assign({}, this.showCommentsMap, { [reviewId]: nextState });
      if (nextState) {
        this.fetchComments(reviewId, 0);
      }
    },
    fetchComments(reviewId, page = 0) {
      if (this.isCommentsLoadingMap[reviewId]) return Promise.resolve();
      this.isCommentsLoadingMap[reviewId] = true;

      return axios.get(`comment/v1/get-by-review/${reviewId}`, {
        params: { page }
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200' && data.result) {
            const rawComments = data.result.content || [];
            rawComments.forEach(c => {
              c.isEdited = c.isEdited || c.edited || c.is_edited ? true : false;
              if (c.replies) {
                c.replies.forEach(r => {
                  r.isEdited = r.isEdited || r.edited || r.is_edited ? true : false;
                });
              }
            });
            this.commentsMap = Object.assign({}, this.commentsMap, { [reviewId]: rawComments });
            this.commentsPageMap[reviewId] = data.result.pageable?.pageNumber || 0;
            this.commentsTotalPagesMap[reviewId] = data.result.totalPages || 0;

            // Update count badge after fetching comments
            const newCommentCount = Number(data.result.totalElements) || 0;
            const reviewIndex = this.reviews.findIndex(r => r.id === reviewId);
            if (reviewIndex !== -1) {
              this.reviews[reviewIndex] = {
                ...this.reviews[reviewIndex],
                commentCount: newCommentCount
              };
            }
            if (this.myReviewData && this.myReviewData.id === reviewId) {
              this.myReviewData.commentCount = newCommentCount;
            }
            if (this.focusedReview && this.focusedReview.id === reviewId) {
              this.focusedReview.commentCount = newCommentCount;
            }
          } else {
            this.commentsMap[reviewId] = [];
            this.commentsPageMap[reviewId] = 0;
            this.commentsTotalPagesMap[reviewId] = 0;
          }
        })
        .catch(error => {
          console.error('Fetch comments error:', error);
          this.commentsMap[reviewId] = [];
        })
        .finally(() => {
          this.isCommentsLoadingMap[reviewId] = false;
          this.$nextTick(() => {
            const root = (this.$el && typeof this.$el?.querySelector === 'function') ? this.$el : document;
            const els = root.querySelectorAll(`[data-review-comments="${reviewId}"]`);
            els.forEach(el => {
              if (this._forceScrollTop) {
                el.scrollTop = 0;
              } else if (this._scrollToRepliesId) {
                const target = el.querySelector(`[data-replies-list="${this._scrollToRepliesId}"]`);
                if (target) {
                  target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
              } else if (this._scrollToCommentId) {
                const target = el.querySelector(`[data-comment-id="${this._scrollToCommentId}"]`);
                if (target) {
                  target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                }
              } else if (this.isLoggedIn && this.currentUserId) {
                const myCommentEl = el.querySelector(`.comment-item[data-user-id="${this.currentUserId}"]`);
                if (myCommentEl) {
                  myCommentEl.scrollIntoView({ block: 'center', behavior: 'smooth' });
                } else {
                  el.scrollTop = 0;
                }
              } else {
                el.scrollTop = 0;
              }
            });
            this._forceScrollTop = false;
            this._scrollToRepliesId = null;
            this._scrollToCommentId = null;
          });
        });
    },
    toggleReplies(commentId) {
      if (this.showRepliesMap[commentId] === undefined) {
        this.showRepliesMap[commentId] = false;
      }
      const nextState = !this.showRepliesMap[commentId];
      this.showRepliesMap[commentId] = nextState;

      if (nextState) {
        // Expanding replies → also show the reply form at bottom
        this.showReplyFormsMap[commentId] = true;
        this.$nextTick(() => {
          // Scroll to the replies list (first reply), not the input
          const root = (this.$el && typeof this.$el?.querySelector === 'function') ? this.$el : document;
          const repliesEl = root.querySelector(`[data-replies-list="${commentId}"]`);
          if (repliesEl) {
            repliesEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        });
      } else {
        // Collapsing → hide reply form and clear input
        this.showReplyFormsMap[commentId] = false;
        this.replyInputsMap[commentId] = '';
      }
    },
    toggleReplyForm(reviewId, commentId) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập để trả lời!');
        this.$router.push('/login');
        return;
      }

      const repliesVisible = !!this.showRepliesMap[commentId];

      if (repliesVisible) {
        // Case 3: Replies already showing → just scroll to the input
        this.$nextTick(() => {
          const root = (this.$el && typeof this.$el?.querySelector === 'function') ? this.$el : document;
          const inputEl = root.querySelector(`[data-reply-input="${commentId}"]`);
          if (inputEl) {
            inputEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            inputEl.focus();
          }
        });
      } else {
        // Case 1: Replies not showing → toggle the reply form (no replies list)
        if (this.showReplyFormsMap[commentId] === undefined) {
          this.showReplyFormsMap[commentId] = false;
        }
        const nextState = !this.showReplyFormsMap[commentId];
        this.showReplyFormsMap[commentId] = nextState;

        if (nextState) {
          this.$nextTick(() => {
            const root = (this.$el && typeof this.$el?.querySelector === 'function') ? this.$el : document;
            const inputEl = root.querySelector(`[data-reply-input="${commentId}"]`);
            if (inputEl) {
              inputEl.focus();
              inputEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
          });
        } else {
          this.replyInputsMap[commentId] = '';
        }
      }
    },
    cancelReply(commentId) {
      this.showReplyFormsMap[commentId] = false;
      this.replyInputsMap[commentId] = '';
    },
    editComment(comment) {
      const { stickers, cleanText } = extractStickersAndText(comment.content, this.funnyStickers);
      this.editingCommentMap[comment.id] = cleanText;
      if (stickers.length > 0) {
        this.selectedStickersMap = {
          ...this.selectedStickersMap,
          ['editComment_' + comment.id]: stickers
        };
      } else {
        this.removeSelectedSticker('editComment_' + comment.id);
      }
      this.isEditingCommentMap[comment.id] = true;
      this.$nextTick(() => {
        const root = (this.$el && typeof this.$el?.querySelector === 'function') ? this.$el : document;
        const el = root.querySelector(`[data-edit-comment-input="${comment.id}"]`);
        if (el) {
          el.focus();
        }
      });
    },
    cancelEditComment(commentId) {
      this.isEditingCommentMap[commentId] = false;
      this.editingCommentMap[commentId] = '';
      this.removeSelectedSticker('editComment_' + commentId);
    },
    updateCommentContent(reviewId, comment) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập!');
        this.$router.push('/login');
        return;
      }

      const stickerString = this.getStickerString('editComment_' + comment.id);
      const newContent = ((this.editingCommentMap[comment.id] || '') + stickerString).trim();
      if (!newContent) return;

      this.isSubmittingEditCommentMap[comment.id] = true;
      const toast = useToast();

      axios.post('comment/create-or-update', {
        id: comment.id,
        content: newContent
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            toast.success(data.message || 'Cập nhật bình luận thành công.');
            comment.content = newContent;
            comment.isEdited = true;
            this.isEditingCommentMap[comment.id] = false;
            this.editingCommentMap[comment.id] = '';
            this.clearSelectedSticker('editComment_' + comment.id);
          } else {
            toast.error(data.message || 'Đã xảy ra lỗi khi cập nhật bình luận.');
          }
        })
        .catch(error => {
          console.error('Update comment error:', error);
          toast.error(error.response?.data?.message || 'Không thể kết nối để cập nhật bình luận.');
        })
        .finally(() => {
          this.isSubmittingEditCommentMap[comment.id] = false;
        });
    },
    confirmDeleteReview(reviewId) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập!');
        this.$router.push('/login');
        return;
      }
      this.deleteConfirmModal = {
        show: true,
        type: 'review',
        reviewId: reviewId,
        commentId: null,
        title: 'Xóa bài nhận xét',
        message: 'Bạn có chắc chắn muốn xóa bài nhận xét này không? Thao tác này không thể hoàn tác.',
        isDeleting: false
      };
    },
    confirmDeleteComment(reviewId, commentId) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập!');
        this.$router.push('/login');
        return;
      }
      this.deleteConfirmModal = {
        show: true,
        type: 'comment',
        reviewId: reviewId,
        commentId: commentId,
        title: 'Xóa bình luận',
        message: 'Bạn có chắc chắn muốn xóa bình luận này không? Các câu trả lời liên quan cũng sẽ bị xóa.',
        isDeleting: false
      };
    },
    closeDeleteModal() {
      if (this.deleteConfirmModal.isDeleting) return;
      this.deleteConfirmModal.show = false;
    },
    executeDelete() {
      if (this.deleteConfirmModal.isDeleting) return;
      this.deleteConfirmModal.isDeleting = true;
      const toast = useToast();

      if (this.deleteConfirmModal.type === 'review') {
        const reviewId = this.deleteConfirmModal.reviewId;
        axios.post(`review/delete/${reviewId}`)
          .then(response => {
            const data = response.data;
            if (data.status === '200') {
              toast.success(data.message || 'Xóa nhận xét thành công.');
              this.myReviewData = null;
              this.hasReviewedGlobal = false;
              this.showWriteReviewForm = true;
              if (this.book && this.book.id) {
                this.fetchBookDetail(this.book.id, true);
                this.fetchBookReviews(this.book.id, 0);
              }
            } else {
              toast.error(data.message || 'Không thể xóa nhận xét.');
            }
          })
          .catch(error => {
            console.error('Delete review error:', error);
            toast.error(error.response?.data?.message || 'Không thể kết nối để xóa nhận xét.');
          })
          .finally(() => {
            this.deleteConfirmModal.isDeleting = false;
            this.deleteConfirmModal.show = false;
          });
      } else if (this.deleteConfirmModal.type === 'comment') {
        const reviewId = this.deleteConfirmModal.reviewId;
        const commentId = this.deleteConfirmModal.commentId;
        axios.post(`comment/delete/${commentId}`)
          .then(response => {
            const data = response.data;
            if (data.status === '200') {
              toast.success(data.message || 'Xóa bình luận thành công.');
              const currentPage = this.commentsPageMap[reviewId] || 0;
              this.fetchComments(reviewId, currentPage);
            } else {
              toast.error(data.message || 'Không thể xóa bình luận.');
            }
          })
          .catch(error => {
            console.error('Delete comment error:', error);
            toast.error(error.response?.data?.message || 'Không thể kết nối để xóa bình luận.');
          })
          .finally(() => {
            this.deleteConfirmModal.isDeleting = false;
            this.deleteConfirmModal.show = false;
          });
      }
    },
    submitReply(reviewId, commentId) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập để trả lời!');
        this.$router.push('/login');
        return;
      }

      const stickerString = this.getStickerString('replyComment_' + commentId);
      const finalContent = ((this.replyInputsMap[commentId] || '') + stickerString).trim();
      if (!finalContent) return;

      this.submittingReplyMap[commentId] = true;
      const toast = useToast();

      axios.post('comment/create-or-update', {
        bookId: this.book.id,
        reviewId: reviewId,
        content: finalContent,
        parentId: commentId
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            this.replyInputsMap[commentId] = '';
            this.clearSelectedSticker('replyComment_' + commentId);
            this.$nextTick(() => this.resetTextareaHeight(document.querySelector(`[data-reply-input="${commentId}"]`)));
            this.showRepliesMap[commentId] = true;
            this._scrollToRepliesId = commentId;
            this._forceScrollTop = true;
            this.fetchComments(reviewId, 0);
          } else {
            toast.error(data.message || 'Đã xảy ra lỗi khi gửi trả lời.');
          }
        })
        .catch(error => {
          console.error('Submit reply error:', error);
          toast.error(error.response?.data?.message || 'Không thể kết nối để gửi trả lời.');
        })
        .finally(() => {
          this.submittingReplyMap[commentId] = false;
        });
    },
    handleInputKeydown(event, submitFn) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        submitFn();
      }
    },
    autoResizeTextarea(event) {
      const el = event.target;
      el.style.height = 'auto';
      el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    },
    resetTextareaHeight(el) {
      if (el) {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
      }
    },
    submitComment(reviewId) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập để bình luận!');
        this.$router.push('/login');
        return;
      }

      const stickerString = this.getStickerString('newComment_' + reviewId);
      const finalContent = ((this.commentInputsMap[reviewId] || '') + stickerString).trim();

      if (!finalContent) return;

      this.isSubmittingCommentMap[reviewId] = true;
      const toast = useToast();

      axios.post('comment/create-or-update', {
        bookId: this.book.id,
        reviewId: reviewId,
        content: finalContent
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            this.commentInputsMap[reviewId] = '';
            this.clearSelectedSticker('newComment_' + reviewId);
            this.$nextTick(() => {
              document.querySelectorAll('.comment-input-field').forEach(el => {
                if (!el.value) el.style.height = 'auto';
              });
            });
            const newCommentId = data.result?.id;
            if (newCommentId) {
              this._scrollToCommentId = newCommentId;
            }
            this._forceScrollTop = true;
            this.fetchComments(reviewId, 0);
          } else {
            toast.error(data.message || 'Đã xảy ra lỗi khi gửi bình luận.');
          }
        })
        .catch(error => {
          console.error('Submit comment error:', error);
          toast.error(error.response?.data?.message || 'Không thể kết nối để gửi bình luận.');
        })
        .finally(() => {
          this.isSubmittingCommentMap[reviewId] = false;
        });
    },
    copyReviewUrl(reviewId) {
      const bookId = this.book?.id || this.$route.params.id;
      if (!bookId || !reviewId) return;

      const origin = window.location.origin;
      const url = `${origin}/book/${bookId}?reviewId=${reviewId}&type=REVIEW`;

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url)
          .then(() => {
            const toast = useToast();
            toast.success('Đã sao chép liên kết vào bộ nhớ tạm!');
          })
          .catch(() => {
            this.fallbackCopyTextToClipboard(url, 'REVIEW');
          });
      } else {
        this.fallbackCopyTextToClipboard(url, 'REVIEW');
      }
    },
    copyCommentUrl(reviewId, commentId, type = 'COMMENT') {
      const bookId = this.book?.id || this.$route.params.id;
      if (!bookId) return;

      const origin = window.location.origin;
      const url = `${origin}/book/${bookId}?reviewId=${reviewId}&refId=${commentId}&type=${type}`;

      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url)
          .then(() => {
            const toast = useToast();
            toast.success('Đã sao chép liên kết vào bộ nhớ tạm!');
          })
          .catch(() => {
            this.fallbackCopyTextToClipboard(url, type);
          });
      } else {
        this.fallbackCopyTextToClipboard(url, type);
      }
    },
    fallbackCopyTextToClipboard(text, type = 'COMMENT') {
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.top = '-9999px';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        const successful = document.execCommand('copy');
        const toast = useToast();
        if (successful) {
          toast.success('Đã sao chép liên kết vào bộ nhớ tạm!');
        } else {
          toast.info('Vui lòng sao chép liên kết: ' + text);
        }
      } catch (err) {
        const toast = useToast();
        toast.info('Vui lòng sao chép liên kết: ' + text);
      }
      document.body.removeChild(textArea);
    },
    interactReview(review, isLike) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập để thực hiện tương tác!');
        this.$router.push('/login');
        return;
      }

      axios.post('review/interact', {
        bookId: this.book.id,
        reviewId: review.id,
        interact: isLike
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            const prev = review.userInteract;
            if (isLike) {
              if (prev === 1) {
                review.userInteract = null;
                review.likeCount = Math.max(0, (review.likeCount || 0) - 1);
              } else {
                review.userInteract = 1;
                review.likeCount = (review.likeCount || 0) + 1;
                if (prev === 0) {
                  review.dislikeCount = Math.max(0, (review.dislikeCount || 0) - 1);
                }
              }
            } else {
              if (prev === 0) {
                review.userInteract = null;
                review.dislikeCount = Math.max(0, (review.dislikeCount || 0) - 1);
              } else {
                review.userInteract = 0;
                review.dislikeCount = (review.dislikeCount || 0) + 1;
                if (prev === 1) {
                  review.likeCount = Math.max(0, (review.likeCount || 0) - 1);
                }
              }
            }
          } else {
            const toast = useToast();
            toast.error(data.message || 'Không thể thực hiện tương tác.');
          }
        })
        .catch(error => {
          console.error('Interact review error:', error);
          const toast = useToast();
          toast.error(error.response?.data?.message || 'Không thể kết nối đến máy chủ.');
        });
    },
    interactComment(comment, isLike) {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập để thực hiện tương tác!');
        this.$router.push('/login');
        return;
      }

      axios.post('comment/interact', {
        commentId: comment.id,
        interact: isLike
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            const prev = comment.userInteract;
            if (isLike) {
              if (prev === 1) {
                comment.userInteract = null;
                comment.likeCount = Math.max(0, (comment.likeCount || 0) - 1);
              } else {
                comment.userInteract = 1;
                comment.likeCount = (comment.likeCount || 0) + 1;
                if (prev === 0) {
                  comment.dislikeCount = Math.max(0, (comment.dislikeCount || 0) - 1);
                }
              }
            } else {
              if (prev === 0) {
                comment.userInteract = null;
                comment.dislikeCount = Math.max(0, (comment.dislikeCount || 0) - 1);
              } else {
                comment.userInteract = 0;
                comment.dislikeCount = (comment.dislikeCount || 0) + 1;
                if (prev === 1) {
                  comment.likeCount = Math.max(0, (comment.likeCount || 0) - 1);
                }
              }
            }
          } else {
            const toast = useToast();
            toast.error(data.message || 'Không thể thực hiện tương tác.');
          }
        })
        .catch(error => {
          console.error('Interact comment error:', error);
          const toast = useToast();
          toast.error(error.response?.data?.message || 'Không thể kết nối đến máy chủ.');
        });
    },
    toggleWriteReviewForm() {
      if (this.hasReviewedGlobal && !this.showWriteReviewForm) {
        const toast = useToast();
        toast.warning('Bạn đã viết nhận xét cho sách này rồi. Bạn chỉ có thể sửa nhận xét cũ bên dưới!');

        const editBtn = document.querySelector('.btn-edit-review');
        if (editBtn) {
          this.scrollToAndHighlightUserReview(editBtn);
        } else {
          // If the edit button is not in current DOM (on page X), fetch page 0 and then locate it
          const bookId = this.book.id;
          this.fetchBookReviews(bookId, 0).then(() => {
            this.$nextTick(() => {
              const newEditBtn = document.querySelector('.btn-edit-review');
              if (newEditBtn) {
                this.scrollToAndHighlightUserReview(newEditBtn);
              } else {
                const el = document.getElementById('reviews-section');
                if (el) {
                  el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }
            });
          });
        }
        return;
      }
      this.showWriteReviewForm = !this.showWriteReviewForm;
      if (!this.showWriteReviewForm) {
        this.userReview.content = '';
        this.userReview.isSpoil = false;
      }
    },
    scrollToAndHighlightUserReview(editBtn) {
      const foundUserReviewEl = editBtn.closest('.review-entry');
      if (foundUserReviewEl) {
        foundUserReviewEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        foundUserReviewEl.classList.add('highlight-review-glow');
        setTimeout(() => {
          foundUserReviewEl.classList.remove('highlight-review-glow');
        }, 2500);
      }
    },
    editReview(review) {
      const { stickers, cleanText } = extractStickersAndText(review.content, this.funnyStickers);
      this.editingReviewContent = cleanText;
      if (stickers.length > 0) {
        this.selectedStickersMap = {
          ...this.selectedStickersMap,
          ['editReview']: stickers
        };
      } else {
        this.removeSelectedSticker('editReview');
      }
      this.editingReviewIsSpoil = review.isSpoil;
      this.isEditingMyReview = true;
      this.showWriteReviewForm = false;
    },

    cancelEditReview() {
      this.isEditingMyReview = false;
      this.editingReviewContent = '';
      this.editingReviewIsSpoil = false;
      this.removeSelectedSticker('editReview');
    },

    updateReview() {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.error('Vui lòng đăng nhập để chỉnh sửa!');
        this.$router.push('/login');
        return;
      }

      const stickerString = this.getStickerString('editReview');
      const finalContent = ((this.editingReviewContent || '') + stickerString).trim();

      if (!finalContent) {
        this.deleteConfirmModal = {
          show: true,
          type: 'review',
          reviewId: this.myReview.id,
          commentId: null,
          title: 'Xóa bài nhận xét',
          message: 'Nội dung nhận xét đang trống. Bạn có muốn xóa bài nhận xét này không? Thao tác này không thể hoàn tác.',
          isDeleting: false
        };
        return;
      }

      this.isSubmitting = true;
      const toast = useToast();

      axios.post('review/create-or-update', {
        bookId: this.book.id,
        content: finalContent,
        spoil: this.editingReviewIsSpoil
      })
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            toast.success(data.message || 'Cập nhật nhận xét thành công.');
            this.isEditingMyReview = false;
            this.editingReviewContent = '';
            this.editingReviewIsSpoil = false;
            this.clearSelectedSticker('editReview');
            this.fetchBookDetail(this.book.id, true);
            this.fetchBookReviews(this.book.id, 0);
            this.fetchMyReview(this.book.id);
            this.autoUpdateReadingStatusToRead();
            updateTrendingCacheIfBookExists(this.book.id);
          } else {
            toast.error(data.message || 'Đã xảy ra lỗi khi cập nhật nhận xét.');
          }
        })
        .catch(error => {
          console.error('Update review error:', error);
          toast.error(error.response?.data?.message || 'Không thể kết nối đến máy chủ.');
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    getAvatarSrc(avatar) {
      if (!avatar) return null;
      const a = String(avatar).trim();
      if (!a || a === 'NO_AVATAR') return null;
      if (a.startsWith('data:') || a.startsWith('http')) return a;
      return 'data:image/png;base64,' + a;
    },
    getAvatarGradientStyle(name) {
      if (!name) return { background: 'linear-gradient(135deg, #2c4a52, #1a3540)' };
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      const h1 = Math.abs(hash) % 360;
      const h2 = (h1 + 60) % 360;
      return {
        background: `linear-gradient(135deg, hsl(${h1}, 70%, 50%), hsl(${h2}, 75%, 42%))`,
        color: '#ffffff',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
      };
    },
    formatCommentTime(timeStr) {
      if (!timeStr) return "";
      const str = String(timeStr).trim();
      if (/^\d{14}$/.test(str)) {
        const yyyy = str.substring(0, 4);
        const mm = str.substring(4, 6);
        const dd = str.substring(6, 8);
        const hh = str.substring(8, 10);
        const min = str.substring(10, 12);
        const ss = str.substring(12, 14);
        return `${hh}:${min}:${ss} ${dd}/${mm}/${yyyy}`;
      }
      if (/^\d{12}$/.test(str)) {
        const yyyy = str.substring(0, 4);
        const mm = str.substring(4, 6);
        const dd = str.substring(6, 8);
        const hh = str.substring(8, 10);
        const min = str.substring(10, 12);
        return `${hh}:${min}:00 ${dd}/${mm}/${yyyy}`;
      }
      if (/^\d{2}:\d{2}\s+\d{2}\/\d{2}\/\d{4}$/.test(str)) {
        const parts = str.split(/\s+/);
        return `${parts[0]}:00 ${parts[1]}`;
      }
      return str;
    },
    fetchBookNote(bookId) {
      this.isLoadingNote = true;
      this.bookNote.bookId = bookId;
      axios.get(`book-note/${bookId}`)
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            if (data.result) {
              const fetchedNote = data.result.note || '';
              const isDefaultNote = !fetchedNote || fetchedNote === '.' || fetchedNote === '-' || fetchedNote.startsWith('Cập nhật trạng thái: ');
              this.bookNote = {
                bookId: data.result.bookId || bookId,
                note: isDefaultNote ? '' : fetchedNote,
                currentPage: data.result.currentPage || 0,
                totalPage: data.result.totalPage || 0,
                percent: data.result.percent || 0.0,
                type: data.result.type || false,
                progressStatus: data.result.progressStatus || 0,
                ownStatus: data.result.ownStatus || 0
              };

              // Only reveal note textarea if note has custom content
              this.showNoteTextarea = !isDefaultNote;
            } else {
              this.bookNote = {
                bookId: bookId,
                note: '',
                currentPage: 0,
                totalPage: 0,
                percent: 0.0,
                type: false,
                progressStatus: 0,
                ownStatus: 0
              };
              this.showNoteTextarea = false;
            }
            this.originalBookNote = JSON.parse(JSON.stringify(this.bookNote));
          }
        })
        .catch(error => {
          console.error('Error fetching book note:', error);
        })
        .finally(() => {
          this.isLoadingNote = false;
        });
    },
    saveBookNote() {
      if (this.noteValidationError) {
        const toast = useToast();
        toast.error(this.noteValidationError);
        return;
      }

      this.isSavingNote = true;
      const toast = useToast();

      let noteToSend = this.bookNote.note ? this.bookNote.note.trim() : '';
      if (!noteToSend) {
        noteToSend = '.';
      }

      let calcPage = this.bookNote.currentPage;
      if (this.bookNote.type && this.bookNote.totalPage > 0) {
        const pct = parseFloat(this.bookNote.percent) || 0;
        calcPage = Math.min(this.bookNote.totalPage, Math.max(0, Math.round((pct / 100) * this.bookNote.totalPage)));
      }

      const status = this.bookNote.progressStatus;
      const isNoProgress = (status === 0 || status === 1);
      const isCompleted = (status === 3);

      const payload = {
        bookId: this.bookNote.bookId,
        note: noteToSend,
        currentPage: isNoProgress ? 0 : (isCompleted ? (this.bookNote.totalPage > 0 ? this.bookNote.totalPage : 0) : calcPage),
        totalPage: isNoProgress ? 0 : (this.bookNote.totalPage || 0),
        percent: isNoProgress ? 0.0 : (isCompleted ? 100.0 : (this.bookNote.type ? parseFloat(this.bookNote.percent) : this.visualPercent)),
        type: this.bookNote.type,
        progressStatus: this.bookNote.progressStatus,
        ownStatus: this.bookNote.ownStatus
      };

      const isStatusOrOwnChanged = !this.originalBookNote ||
        Number(this.originalBookNote.progressStatus) !== Number(payload.progressStatus) ||
        Number(this.originalBookNote.ownStatus) !== Number(payload.ownStatus);

      axios.post('book-note/save', payload)
        .then(response => {
          const data = response.data;
          if (data.status === '200') {
            toast.success(data.message || 'Cập nhật ghi chú cá nhân thành công.');
            if (isStatusOrOwnChanged) {
              updateTrendingCacheIfBookExists(this.bookNote.bookId);
            }
            this.fetchBookNote(this.bookNote.bookId);
            if (this.book && this.book.id) {
              if (typeof this.fetchBookDetail === 'function') this.fetchBookDetail(this.book.id, true);
              if (typeof this.fetchBookReviews === 'function') this.fetchBookReviews(this.book.id, 0);
              if (typeof this.fetchMyReview === 'function') this.fetchMyReview(this.book.id);
              if (typeof this.fetchMyEvaluate === 'function') this.fetchMyEvaluate(this.book.id);
            }
            this.closeBookNoteModal();
          } else {
            toast.error(data.message || 'Đã xảy ra lỗi khi lưu ghi chú cá nhân.');
          }
        })
        .catch(error => {
          console.error('Error saving book note:', error);
          toast.error(error.response?.data?.message || 'Không thể kết nối đến máy chủ.');
        })
        .finally(() => {
          this.isSavingNote = false;
        });
    },
    calculatePercent() {
      if (!this.bookNote.type) {
        if (this.bookNote.totalPage > 0) {
          const pct = (this.bookNote.currentPage / this.bookNote.totalPage) * 100;
          this.bookNote.percent = Math.round(pct * 10) / 10;
        } else {
          this.bookNote.percent = 0;
        }
      }
    },
    calculatePageFromPercent() {
      if (this.bookNote.type && this.bookNote.totalPage > 0) {
        const pct = parseFloat(this.bookNote.percent) || 0;
        const page = Math.round((pct / 100) * this.bookNote.totalPage);
        this.bookNote.currentPage = Math.min(this.bookNote.totalPage, Math.max(0, page));
      }
    },
    onPercentInput(e) {
      if (e && e.target) {
        let val = parseFloat(e.target.value);
        if (isNaN(val)) val = 0;
        if (val > 100) val = 100;
        if (val < 0) val = 0;
        this.bookNote.percent = val;
      }
      this.calculatePageFromPercent();
    },
    setNoteType(isPercent) {
      this.bookNote.type = isPercent;
      if (isPercent) {
        this.calculatePercent();
      } else {
        if (this.bookNote.totalPage > 0) {
          this.calculatePageFromPercent();
        }
      }
    },
    openBookNoteModal() {
      if (!this.isLoggedIn) {
        const toast = useToast();
        toast.warning('Vui lòng đăng nhập để sử dụng tính năng ghi chú & tiến độ!');
        this.$router.push('/login');
        return;
      }
      this.showNoteModal = true;
      if (this.book?.id) {
        this.fetchBookNote(this.book.id);
      }
    },
    closeBookNoteModal() {
      this.showNoteModal = false;
      if (this.originalBookNote) {
        this.bookNote = JSON.parse(JSON.stringify(this.originalBookNote));
        const noteText = this.bookNote.note || '';
        const isDefaultNote = !noteText || noteText === '.' || noteText === '-' || noteText.startsWith('Cập nhật trạng thái: ');
        this.showNoteTextarea = !isDefaultNote;
      }
    },
    openNoteTextarea() {
      this.showNoteTextarea = true;
      if (!this.bookNote.note && this.originalBookNote?.note) {
        this.bookNote.note = this.originalBookNote.note;
      }
    },
    hideNoteTextarea() {
      this.showNoteTextarea = false;
      const originalNoteText = this.originalBookNote?.note || '';
      if (originalNoteText) {
        // If note exists from DB, restore original note when hidden
        this.bookNote.note = originalNoteText;
      } else {
        // If it's a newly typed draft note, reset it
        this.bookNote.note = '';
      }
    },
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    },
    toggleReadingStatus() {
      if (!this.isMobile) return;
      this.collapseReadingStatus = !this.collapseReadingStatus;
    },
    toggleOwnership() {
      if (!this.isMobile) return;
      this.collapseOwnership = !this.collapseOwnership;
    },
    selectProgressStatus(value) {
      this.bookNote.progressStatus = Number(value);
    },
    autoUpdateReadingStatusToRead() {
      if (!this.isLoggedIn || !this.book || !this.book.id) return;
      if (!this.bookNote || this.bookNote.progressStatus === 0 || this.bookNote.progressStatus === 1) {
        let existingNote = (this.bookNote && this.bookNote.note) ? this.bookNote.note.trim() : '';
        if (!existingNote && this.originalBookNote?.note) {
          existingNote = this.originalBookNote.note.trim();
        }
        let noteToSend = existingNote || '.';
        const payload = {
          bookId: this.book.id,
          note: noteToSend,
          currentPage: 0,
          totalPage: 0,
          percent: 100.0,
          type: this.bookNote ? this.bookNote.type : false,
          progressStatus: 3, // 3 = Đã đọc xong
          ownStatus: this.bookNote ? this.bookNote.ownStatus : 0
        };

        axios.post('book-note/save', payload)
          .then(response => {
            if (response.data && response.data.status === '200') {
              const toast = useToast();
              toast.success('Chuyển trạng thái sang Đã đọc xong.');
              updateTrendingCacheIfBookExists(this.book.id);
              this.fetchBookNote(this.book.id);
            }
          })
          .catch(error => {
            console.error('Auto update reading status error:', error);
          });
      }
    }
  },
  unmounted() {
    if (this.head && typeof this.head.dispose === 'function') {
      this.head.dispose();
    }
  }
};